import { Switch as RadixSwitch } from "radix-ui";
import { type ReactNode, useId } from "react";
import { cn } from "../lib/cn";

export interface SwitchProps {
	checked?: boolean;
	defaultChecked?: boolean;
	onCheckedChange?: (checked: boolean) => void;
	label?: ReactNode;
	/** Вторая строка подписи: чем обернётся выключение согласия. */
	description?: ReactNode;
	disabled?: boolean;
	name?: string;
	id?: string;
	className?: string;
}

/**
 * Тумблер 40×24: дорожка r-full, шайба 20px с мягкой тенью. Единственный
 * способ показать бинарную опцию в системе — чекбокс оставлен согласиям.
 *
 * Подпись стоит справа и занимает всю оставшуюся ширину, поэтому в списке
 * настроек тумблеры выстраиваются в колонку по правому краю.
 */
export function Switch({
	checked,
	defaultChecked,
	onCheckedChange,
	label,
	description,
	disabled,
	name,
	id,
	className,
}: SwitchProps) {
	const autoId = useId();
	const switchId = id ?? autoId;
	return (
		// `group` — ховер и курсор на всей строке: кликается и подпись тоже
		// (`<label htmlFor>`), значит и реагировать строка обязана целиком.
		<div className={cn("group flex items-center gap-2.5", className)}>
			<RadixSwitch.Root
				id={switchId}
				checked={checked}
				defaultChecked={defaultChecked}
				onCheckedChange={onCheckedChange}
				disabled={disabled}
				name={name}
				className={cn(
					"inline-flex h-6 w-10 shrink-0 items-center rounded-full p-0.5",
					"bg-line transition-colors duration-[var(--dur-300)] ease-elegant",
					"data-[state=checked]:bg-brand",
					"focus-ring cursor-pointer",
					// Ховер затемняет дорожку — и включённую, и выключенную.
					"group-hover:not-disabled:data-[state=checked]:bg-brand-hover",
					"group-hover:not-disabled:data-[state=unchecked]:bg-btn-secondary-hover",
					"disabled:cursor-not-allowed disabled:opacity-60",
				)}
			>
				<RadixSwitch.Thumb
					className={cn(
						"block size-5 rounded-full bg-card shadow-knob",
						"transition-transform duration-[var(--dur-300)] ease-elegant",
						"data-[state=checked]:translate-x-4",
					)}
				/>
			</RadixSwitch.Root>
			{label != null || description != null ? (
				<label
					htmlFor={switchId}
					className={cn(
						"flex flex-col gap-0.5 select-none transition-colors duration-150 ease-smooth",
						disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer group-hover:text-fg-muted",
					)}
				>
					{label != null ? <span className="t-body">{label}</span> : null}
					{description != null ? <span className="t-meta text-fg-muted">{description}</span> : null}
				</label>
			) : null}
		</div>
	);
}
